import React, { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp, TrendingDown, Heart, MessageCircle, Building2, Share2, BarChart3, Image, Smile, Activity, X, Newspaper, ExternalLink, Clock, FileText } from 'lucide-react';
import { auth, db } from '../firebase/config';
import { collection, addDoc, getDocs, query, where, orderBy, limit, updateDoc, doc, arrayUnion, arrayRemove, serverTimestamp, getDoc } from 'firebase/firestore';
import { canCreatePost, canLikePost, USER_ROLES } from '../utils/roles';
import { getDefaultAvatar } from '../utils/avatars';
import UserProfilePopup from './UserProfilePopup';
import AdSense from './AdSense';
import NewsSummaryModal from './NewsSummaryModal';
import ArticleContentModal from './ArticleContentModal';
import WatchlistButton from './WatchlistButton';

export default function ThemeDetail({ themeKey, guestTheme, onBack, onStockClick }) {
  console.log('[ThemeDetail] Component mounted/rendered with themeKey:', themeKey);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [newIdea, setNewIdea] = useState('');
  const [newIdeaSentiment, setNewIdeaSentiment] = useState('bullish');
  const [newIdeaTicker, setNewIdeaTicker] = useState('');
  const [newPostImages, setNewPostImages] = useState([]);
  const [newPostFeeling, setNewPostFeeling] = useState('');
  const [newPostActivity, setNewPostActivity] = useState('');
  const [showFeelingPicker, setShowFeelingPicker] = useState(false);
  const [showActivityPicker, setShowActivityPicker] = useState(false);
  const [userRole, setUserRole] = useState(USER_ROLES.GUEST);
  const [userAvatar, setUserAvatar] = useState(getDefaultAvatar());
  const [companies, setCompanies] = useState([]);
  const [showNewPost, setShowNewPost] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [theme, setTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);
  const [themeMembers, setThemeMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [summaryArticle, setSummaryArticle] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const feelings = [
    { emoji: '😊', label: '행복해요' },
    { emoji: '😍', label: '사랑해요' },
    { emoji: '😂', label: '웃겨요' },
    { emoji: '🥰', label: '설레요' },
    { emoji: '😎', label: '멋져요' },
    { emoji: '🤗', label: '감사해요' },
    { emoji: '😢', label: '슬퍼요' },
    { emoji: '😤', label: '화나요' },
    { emoji: '😴', label: '피곤해요' },
    { emoji: '🤔', label: '생각중이에요' },
    { emoji: '😋', label: '맛있어요' },
    { emoji: '🎉', label: '축하해요' },
    { emoji: '😌', label: '평화로워요' },
    { emoji: '😆', label: '신나요' },
    { emoji: '🥳', label: '파티해요' },
    { emoji: '😇', label: '천사같아요' },
    { emoji: '🤩', label: '감동했어요' },
    { emoji: '😱', label: '놀랐어요' },
    { emoji: '🥺', label: '애틋해요' },
    { emoji: '😭', label: '울어요' },
    { emoji: '😅', label: '민망해요' },
    { emoji: '🤤', label: '군침도네요' }
  ];

  const activities = [
    { emoji: '✈️', label: '여행 중' },
    { emoji: '🏋️', label: '운동 중' },
    { emoji: '📚', label: '공부 중' },
    { emoji: '🍽️', label: '식사 중' },
    { emoji: '☕', label: '카페에서' },
    { emoji: '🎮', label: '게임 중' },
    { emoji: '🎬', label: '영화 관람 중' },
    { emoji: '🎵', label: '음악 듣는 중' },
    { emoji: '🛒', label: '쇼핑 중' },
    { emoji: '🏠', label: '집에서' },
    { emoji: '💼', label: '일하는 중' },
    { emoji: '🚗', label: '이동 중' },
    { emoji: '🎨', label: '그림 그리는 중' },
    { emoji: '🍳', label: '요리하는 중' },
    { emoji: '📖', label: '독서 중' },
    { emoji: '🧘', label: '명상 중' },
    { emoji: '🏃', label: '달리는 중' },
    { emoji: '🎤', label: '노래하는 중' },
    { emoji: '💻', label: '코딩 중' },
    { emoji: '🎯', label: '목표 달성 중' },
    { emoji: '🌅', label: '산책 중' },
    { emoji: '🛌', label: '휴식 중' }
  ];

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const imageUrls = files.map(file => URL.createObjectURL(file));
    setNewPostImages(prev => [...prev, ...imageUrls]);
  };

  const removeImage = (index) => {
    setNewPostImages(prev => prev.filter((_, i) => i !== index));
  };

  // Load theme from Firebase
  useEffect(() => {
    const loadTheme = async () => {
      if (!themeKey) return;

      try {
        setThemeLoading(true);
        console.log('[ThemeDetail] Loading theme with key:', themeKey);
        const themeDoc = await getDoc(doc(db, 'investmentThemes', themeKey));
        // Check if this is a guest theme first
        if (guestTheme && themeKey === guestTheme.id) {
          console.log('[ThemeDetail] Using guest theme:', guestTheme);
          setTheme({
            name: guestTheme.name,
            description: guestTheme.description,
            companies: guestTheme.companies,
            createdBy: null
          });
          setThemeLoading(false);
          return;
        }

        if (themeDoc.exists()) {
          const themeData = themeDoc.data();
          console.log('[ThemeDetail] Theme data loaded:', themeData);

          // Handle both data structures:
          // - Old structure: tickers array (for default themes)
          // - New structure: stocks array with {ticker, name, ...} objects (for custom themes)
          let companiesList = [];
          if (themeData.tickers && Array.isArray(themeData.tickers)) {
            // Old structure: tickers is an array of strings
            companiesList = themeData.tickers;
            console.log('[ThemeDetail] Using tickers field:', companiesList);
          } else if (themeData.stocks && Array.isArray(themeData.stocks)) {
            // New structure: stocks is an array of objects
            companiesList = themeData.stocks.map(stock => stock.ticker);
            console.log('[ThemeDetail] Using stocks field:', companiesList);
          } else {
            console.log('[ThemeDetail] No tickers or stocks field found in theme data');
          }

          const themeObject = {
            name: themeData.name,
            description: themeData.description,
            companies: companiesList,
            createdBy: themeData.createdBy
          };
          console.log('[ThemeDetail] Setting theme object:', themeObject);
          setTheme(themeObject);
        } else {
          console.log('[ThemeDetail] Theme document does not exist');
        }
      } catch (error) {
        console.error('[ThemeDetail] Error loading theme:', error);
      } finally {
        setThemeLoading(false);
      }
    };

    loadTheme();
  }, [themeKey, guestTheme]);

  useEffect(() => {
    loadUserRole();
  }, []);

  useEffect(() => {
    if (themeKey && theme) {
      loadCompaniesData();
      loadNews();
      loadPosts();
      loadThemeMembers();
    }
  }, [themeKey, theme]);

  const loadThemeMembers = async () => {
    setMembersLoading(true);
    try {
      // Get all users who have this theme in their watchlist
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);

      const members = [];
      const themeLeaderId = theme.createdBy; // The creator is the theme leader

      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        const watchlistThemes = userData.watchlistThemes || [];

        if (watchlistThemes.includes(themeKey)) {
          const isLeader = doc.id === themeLeaderId;
          members.push({
            id: doc.id,
            displayName: userData.displayName || 'User',
            username: userData.username || `@user${doc.id.substring(0, 6)}`,
            avatar: userData.avatar || getDefaultAvatar(),
            userRole: userData.userRole || USER_ROLES.MEMBER,
            isThemeLeader: isLeader
          });
        }
      });

      // Sort members: theme leader first, then by display name
      members.sort((a, b) => {
        if (a.isThemeLeader) return -1;
        if (b.isThemeLeader) return 1;
        return a.displayName.localeCompare(b.displayName);
      });

      setThemeMembers(members);
    } catch (error) {
      console.error('Error loading theme members:', error);
    } finally {
      setMembersLoading(false);
    }
  };

  const loadUserRole = async () => {
    const user = auth.currentUser;
    if (!user) {
      setUserRole(USER_ROLES.GUEST);
      setUserAvatar(getDefaultAvatar());
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserRole(userData.userRole || USER_ROLES.MEMBER);
        setUserAvatar(userData.avatar || getDefaultAvatar());
      } else {
        setUserRole(USER_ROLES.MEMBER);
        setUserAvatar(getDefaultAvatar());
      }
    } catch (error) {
      console.error('Error loading user role:', error);
      setUserRole(USER_ROLES.MEMBER);
      setUserAvatar(getDefaultAvatar());
    }
  };

  const showSummary = (article) => {
    setSummaryArticle(article);
    setShowSummaryModal(true);
  };

  const loadCompaniesData = async () => {
    try {
      console.log('[ThemeDetail] Loading companies data...');
      console.log('[ThemeDetail] Theme companies list:', theme.companies);
      const response = await fetch(`${API_URL}/api/companies`);
      const data = await response.json();
      console.log('[ThemeDetail] API response:', data.success ? `${data.count} companies loaded` : 'Failed');
      if (data.success) {
        // Filter to only show companies in this theme
        const themeCompanies = data.companies.filter(c =>
          theme.companies.includes(c.ticker)
        );
        console.log('[ThemeDetail] Filtered theme companies:', themeCompanies.length, 'out of', theme.companies.length);
        console.log('[ThemeDetail] Theme companies found:', themeCompanies.map(c => c.ticker).join(', '));
        setCompanies(themeCompanies);
      }
    } catch (error) {
      console.error('[ThemeDetail] Error loading companies:', error);
    }
  };

  const loadNews = async () => {
    setLoading(true);
    try {
      console.log('[ThemeDetail] Loading news for theme:', theme);
      console.log('[ThemeDetail] Companies in theme:', theme.companies);

      if (!theme.companies || theme.companies.length === 0) {
        console.log('[ThemeDetail] No companies found in theme');
        setNews([]);
        setLoading(false);
        return;
      }

      // Load news from Firestore for all companies in the theme
      const newsPromises = theme.companies.slice(0, 20).map(async (ticker) => {
        console.log('[ThemeDetail] Fetching news for ticker:', ticker);
        const newsRef = collection(db, 'news');
        const q = query(
          newsRef,
          where('ticker', '==', ticker),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const snapshot = await getDocs(q);
        console.log(`[ThemeDetail] Found ${snapshot.docs.length} news items for ${ticker}`);
        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            publishedAt: typeof data.publishedAt === 'string'
              ? new Date(data.publishedAt)
              : data.publishedAt?.toDate?.() || new Date(),
            date: data.createdAt?.toDate?.() || new Date()
          };
        });
      });

      const newsArrays = await Promise.all(newsPromises);
      const allNews = newsArrays.flat();
      console.log('[ThemeDetail] Total news items loaded:', allNews.length);

      // Sort by publishedAt or createdAt
      allNews.sort((a, b) => b.publishedAt - a.publishedAt);

      setNews(allNews.slice(0, 50));
      console.log('[ThemeDetail] News set to state:', allNews.slice(0, 50).length);
    } catch (error) {
      console.error('[ThemeDetail] Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    setPostsLoading(true);
    try {
      // Load posts for this theme
      const postsRef = collection(db, 'posts');
      const q = query(
        postsRef,
        where('themeKey', '==', themeKey),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setPostsLoading(false);
    }
  };

  const handleShareIdea = async () => {
    if (!newIdea.trim()) {
      alert('Please enter your idea!');
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      alert('Please login to share ideas!');
      return;
    }

    if (!canCreatePost(userRole)) {
      if (userRole === USER_ROLES.SUSPEND) {
        alert('Your account has been suspended. You cannot create posts.');
      } else if (userRole === USER_ROLES.GUEST) {
        alert('Guest accounts cannot create posts. Please contact an administrator to upgrade your account.');
      } else {
        alert('You do not have permission to create posts.');
      }
      return;
    }

    try {
      const postData = {
        authorId: user.uid,
        authorName: user.displayName || user.email,
        authorAvatar: userAvatar,
        content: newIdea,
        prediction: newIdeaSentiment,
        themeKey: themeKey,
        themeName: theme.name,
        images: newPostImages,
        feeling: newPostFeeling,
        activity: newPostActivity,
        likes: [],
        comments: [],
        shares: 0,
        createdAt: serverTimestamp()
      };

      // Add ticker if one was selected
      if (newIdeaTicker) {
        postData.ticker = newIdeaTicker;
      }

      await addDoc(collection(db, 'posts'), postData);
      setNewIdea('');
      setNewIdeaSentiment('bullish');
      setNewIdeaTicker('');
      setNewPostImages([]);
      setNewPostFeeling('');
      setNewPostActivity('');
      setShowNewPost(false);
      loadPosts();
      alert('Your idea has been shared!');
    } catch (error) {
      console.error('Error sharing idea:', error);
      alert('Failed to share idea: ' + error.message);
    }
  };

  const handleLike = async (postId, likes) => {
    const user = auth.currentUser;
    if (!user) {
      alert('Please login to like posts!');
      return;
    }

    if (!canLikePost(userRole)) {
      alert('You do not have permission to like posts.');
      return;
    }

    try {
      const postRef = doc(db, 'posts', postId);
      const hasLiked = likes.includes(user.uid);
      if (hasLiked) {
        await updateDoc(postRef, { likes: arrayRemove(user.uid) });
      } else {
        await updateDoc(postRef, { likes: arrayUnion(user.uid) });
      }
      loadPosts();
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now';

    let date;
    if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      return 'Just now';
    }

    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return Math.floor(seconds / 60) + 'm ago';
    if (seconds < 86400) return Math.floor(seconds / 3600) + 'h ago';
    return Math.floor(seconds / 86400) + 'd ago';
  };

  if (themeLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading theme...</p>
        </div>
      </div>
    );
  }

  if (!theme) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center">
          <p className="text-red-600">Theme not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{theme.name}</h1>
              <p className="text-gray-600 mb-4">{theme.description}</p>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Building2 className="w-4 h-4" />
                <span>{theme.companies.length} companies</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Sidebar - Companies (3 columns) */}
        <div className="lg:col-span-3 space-y-6">
          {/* Companies in Theme */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{theme.name} 종목</h2>
            <div className="space-y-2">
              {companies.length === 0 && theme.companies && theme.companies.length > 0 ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-3">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-gray-600 text-sm">Loading stock data...</p>
                  <p className="text-gray-500 text-xs mt-1">{theme.companies.length} stocks in this theme</p>
                </div>
              ) : companies.length === 0 ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
                    <Building2 className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-600 text-sm">No stocks in this theme</p>
                </div>
              ) : (
                companies.map((company) => (
                  <div key={company.ticker} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
                    <div onClick={() => onStockClick?.(company.ticker)} className="flex-1 cursor-pointer">
                      <span className="font-semibold text-blue-600">{company.ticker}</span>
                      <span className="text-gray-900 ml-2 text-sm">{company.name}</span>
                      {company.sector && (
                        <span className="text-xs text-gray-500 ml-2">{company.sector}</span>
                      )}
                    </div>
                    <WatchlistButton
                      ticker={company.ticker}
                      userId={auth.currentUser?.uid}
                      userRole={userRole}
                      size="sm"
                      className="ml-2"
                    />
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Theme Members */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              멤버 ({themeMembers.length})
            </h2>
            {membersLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            ) : themeMembers.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500 text-sm">아직 멤버가 없습니다</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {themeMembers.map((member) => (
                  <div
                    key={member.id}
                    onClick={() => setSelectedUserId(member.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      member.isThemeLeader
                        ? 'bg-gradient-to-r from-yellow-50 to-amber-50 hover:from-yellow-100 hover:to-amber-100 border-2 border-yellow-300'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 flex-shrink-0">
                        {member.avatar && member.avatar.startsWith('data:image') ? (
                          <img src={member.avatar} alt={member.displayName} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-2xl">{member.avatar}</span>
                        )}
                      </div>
                      {member.isThemeLeader && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center border-2 border-white">
                          <span className="text-xs">👑</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm truncate">
                        {member.displayName}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {member.username}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        member.isThemeLeader
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {member.isThemeLeader ? 'Leader' : 'Follower'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Center - Community Ideas (6 columns) */}
        <div className="lg:col-span-6 space-y-6">
          {/* Share Idea */}
          {canCreatePost(userRole) && (
            <div className="bg-white rounded-lg shadow p-4">
              <button
                onClick={() => setShowNewPost(true)}
                className="w-full text-left px-4 py-3 bg-gray-100 rounded-full text-gray-500 hover:bg-gray-200"
              >
                {auth.currentUser?.displayName || auth.currentUser?.email}, 무슨 생각을 하고 계신가요?
              </button>
            </div>
          )}

          {/* Community Ideas */}
          <div className="space-y-4">
            {postsLoading ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-500">No ideas shared yet. Be the first!</p>
              </div>
            ) : (
              posts.map((post, index) => {
                const isLiked = auth.currentUser && post.likes?.includes(auth.currentUser.uid);
                const showAdAfter = (index + 1) % 3 === 0; // Show ad every 3 posts
                return (
                  <React.Fragment key={post.id}>
                    <article className="bg-white rounded-lg shadow p-4">
                    <div className="flex gap-3">
                      {/* Left side: Avatar, User name, Stock ticker, and Prediction */}
                      <div className="flex flex-col gap-2">
                        {/* Line 1: Avatar + User name (centered relative to line 2) */}
                        <div className="flex items-center justify-center gap-4">
                          <div
                            className="text-3xl cursor-pointer hover:opacity-80 transition"
                            onClick={() => setSelectedUserId(post.authorId)}
                            title="View profile"
                          >
                            {post.authorAvatar}
                          </div>
                          <div>
                            <h4 className="font-bold text-sm">{post.authorName}</h4>
                            <p className="text-xs text-gray-500">{getTimeAgo(post.createdAt)}</p>
                          </div>
                        </div>
                        {/* Line 2: Stock ticker + Prediction */}
                        <div className="flex items-center gap-2">
                          {post.ticker && (
                            <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700">${post.ticker}</span>
                          )}
                          <span className={'inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ' + (post.prediction === 'bullish' ? 'text-green-500 bg-green-500/10' : post.prediction === 'bearish' ? 'text-red-500 bg-red-500/10' : 'text-yellow-500 bg-yellow-500/10')}>
                            {post.prediction === 'bullish' ? <TrendingUp className="w-4 h-4" /> : post.prediction === 'bearish' ? <TrendingDown className="w-4 h-4" /> : <BarChart3 className="w-4 h-4" />}
                            {post.prediction === 'bullish' ? '강세' : post.prediction === 'bearish' ? '약세' : '중립'}
                          </span>
                        </div>
                      </div>
                      {/* Right side: Content */}
                      <div className="flex-1">
                        <p className="leading-relaxed text-gray-800">{post.content}</p>

                        {/* Feeling/Activity Tags */}
                        {(post.feeling || post.activity) && (
                          <div className="mt-2 flex gap-2">
                            {post.feeling && (
                              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs flex items-center gap-1">
                                <Smile className="w-3 h-3" />
                                {post.feeling}
                              </span>
                            )}
                            {post.activity && (
                              <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs flex items-center gap-1">
                                <Activity className="w-3 h-3" />
                                {post.activity}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Images */}
                        {post.images && post.images.length > 0 && (
                          <div className={`mt-3 grid gap-2 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                            {post.images.map((img, index) => (
                              <img
                                key={index}
                                src={img}
                                alt={`Post image ${index + 1}`}
                                className="w-full rounded-lg object-cover"
                                style={{ maxHeight: post.images.length === 1 ? '400px' : '200px' }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Bottom: Stats and Actions */}
                    <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
                      <span>{(post.likes || []).length}명이 좋아합니다</span>
                      <span>댓글 {(post.comments || []).length}개</span>
                    </div>
                    <div className="pt-2 border-t border-gray-200 flex items-center justify-around">
                      <button onClick={() => handleLike(post.id, post.likes || [])} className={'flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 ' + ((post.likes || []).includes(auth.currentUser?.uid) ? 'text-red-500' : 'text-gray-600')}>
                        <Heart className={'w-5 h-5 ' + ((post.likes || []).includes(auth.currentUser?.uid) ? 'fill-current' : '')} />
                        <span className="font-medium">좋아요</span>
                      </button>
                      <button className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 text-gray-600"><MessageCircle className="w-5 h-5" /><span className="font-medium">댓글</span></button>
                      <button className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 text-gray-600"><Share2 className="w-5 h-5" /><span className="font-medium">공유</span></button>
                    </div>
                    </article>
                  {showAdAfter && (
                    <div className="bg-white rounded-lg shadow p-4">
                      <AdSense
                        slot="5107198814"
                      />
                    </div>
                  )}
                  </React.Fragment>
                );
              })
            )}
          </div>
        </div>

        {/* Right Sidebar - News (3 columns) */}
        <div className="lg:col-span-3 space-y-6">
          {/* Latest News */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">최신 뉴스</h2>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            ) : news.length === 0 ? (
              <p className="text-gray-500 text-center py-8">뉴스가 없습니다</p>
            ) : (
              <div className="space-y-3">
                {news.slice(0, 10).map((item, index) => (
                  <article
                    key={index}
                    className="border-b border-gray-100 pb-3 last:border-0 hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <Newspaper className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />

                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm mb-1 text-gray-900 line-clamp-2">
                          {item.title}
                        </h4>

                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                            {item.ticker}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(item.date || item.publishedAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                          </span>
                          {item.source && (
                            <span
                              className="flex items-center gap-1"
                              style={{
                                color: item.isOfficialSource ? '#10b981' : '#9ca3af',
                                fontWeight: item.isOfficialSource ? '600' : '400'
                              }}
                            >
                              {item.isOfficialSource && '🏢'}
                              {item.source}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => showSummary(item)}
                            className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium"
                          >
                            <FileText className="w-3 h-3" />
                            요약
                          </button>


                          <button
                            onClick={() => setSelectedArticle(item)}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium"
                          >
                            원문 보기
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Profile Popup */}
      {selectedUserId && (
        <UserProfilePopup
          userId={selectedUserId}
          currentUserId={auth.currentUser?.uid}
          onClose={() => setSelectedUserId(null)}
        />
      )}

      {/* New Post Modal */}
      {showNewPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowNewPost(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-4 relative" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">게시물 작성</h2>
              <button
                onClick={() => setShowNewPost(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Top Section: User Info and Stock/Prediction Selection */}
              <div className="flex gap-4 mb-4">
                {/* User Info */}
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                    <span className="text-3xl">{userAvatar}</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-base">{auth.currentUser?.displayName || auth.currentUser?.email}</h4>
                    <p className="text-sm text-gray-500">공개 게시물</p>
                  </div>
                </div>

                {/* Stock Selection and Prediction */}
                <div className="flex gap-8 ml-auto">
                  <div>
                    <select
                      value={newIdeaTicker}
                      onChange={(e) => setNewIdeaTicker(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">종목 선택</option>
                      {theme.companies.map((ticker) => (
                        <option key={ticker} value={ticker}>{ticker}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <select
                      value={newIdeaSentiment}
                      onChange={(e) => setNewIdeaSentiment(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="bullish">🟢 강세</option>
                      <option value="neutral">🟡 중립</option>
                      <option value="bearish">🔴 약세</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Post Content */}
              <textarea
                value={newIdea}
                onChange={(e) => setNewIdea(e.target.value)}
                placeholder="무슨 생각을 하고 계신가요?"
                className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 leading-relaxed resize-none"
                rows="6"
                autoFocus
              />

              {/* Image Preview */}
              {newPostImages.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {newPostImages.map((img, index) => (
                    <div key={index} className="relative">
                      <img src={img} alt={`Upload ${index + 1}`} className="w-full h-48 object-cover rounded-lg" />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1 bg-gray-800 bg-opacity-70 text-white rounded-full hover:bg-opacity-90"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Feeling/Activity Display */}
              {(newPostFeeling || newPostActivity) && (
                <div className="mt-3 flex gap-2">
                  {newPostFeeling && (
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm flex items-center gap-1">
                      <Smile className="w-4 h-4" />
                      {newPostFeeling}
                      <button onClick={() => setNewPostFeeling('')} className="ml-1 hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {newPostActivity && (
                    <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm flex items-center gap-1">
                      <Activity className="w-4 h-4" />
                      {newPostActivity}
                      <button onClick={() => setNewPostActivity('')} className="ml-1 hover:text-green-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              )}

              {/* Add to Post Options */}
              <div className="mt-4 p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">게시물에 추가:</span>
                  <div className="flex gap-2">
                    {/* Photo/GIF Upload */}
                    <label className="cursor-pointer p-2 hover:bg-gray-100 rounded-lg transition" title="사진/GIF">
                      <Image className="w-5 h-5 text-green-600" />
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>

                    {/* Feeling */}
                    <button
                      onClick={() => {
                        setShowFeelingPicker(!showFeelingPicker);
                        setShowActivityPicker(false);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition"
                      title="기분"
                    >
                      <Smile className="w-5 h-5 text-yellow-600" />
                    </button>

                    {/* Activity */}
                    <button
                      onClick={() => {
                        setShowActivityPicker(!showActivityPicker);
                        setShowFeelingPicker(false);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition"
                      title="활동"
                    >
                      <Activity className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Feeling Picker Dropdown - Centered in Modal */}
            {showFeelingPicker && (
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 z-30">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">기분을 선택하세요</h4>
                <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                  {feelings.map((feeling) => (
                    <button
                      key={feeling.label}
                      onClick={() => {
                        setNewPostFeeling(`${feeling.emoji} ${feeling.label}`);
                        setShowFeelingPicker(false);
                      }}
                      className="flex items-center gap-2 p-3 hover:bg-gray-100 rounded-lg transition text-left"
                    >
                      <span className="text-2xl">{feeling.emoji}</span>
                      <span className="text-sm">{feeling.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Activity Picker Dropdown - Centered in Modal */}
            {showActivityPicker && (
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 z-30">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">활동을 선택하세요</h4>
                <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                  {activities.map((activity) => (
                    <button
                      key={activity.label}
                      onClick={() => {
                        setNewPostActivity(`${activity.emoji} ${activity.label}`);
                        setShowActivityPicker(false);
                      }}
                      className="flex items-center gap-2 p-3 hover:bg-gray-100 rounded-lg transition text-left"
                    >
                      <span className="text-2xl">{activity.emoji}</span>
                      <span className="text-sm">{activity.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowNewPost(false)}
                className="px-6 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition font-medium"
              >
                취소
              </button>
              <button
                onClick={handleShareIdea}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                게시
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Article Content Modal */}
      {selectedArticle && (
        <ArticleContentModal
          article={selectedArticle}
          onClose={() => setSelectedArticle(null)}
        />
      )}

      {/* Summary Modal */}
      <NewsSummaryModal
        article={summaryArticle}
        onClose={() => {
          setShowSummaryModal(false);
          setSummaryArticle(null);
        }}
      />
    </div>
  );
}
